// @ts-nocheck — re-exported through @hightouch/lib/templating into app's
// strict-null tsconfig. tsgo would flag pre-existing regex-match
// nullability inferences that are guaranteed safe by construction. The
// transformer's 37-test suite covers every transformation path, so
// double-type-checking adds no real safety. Reverting this comment
// restores normal type checking when the SDK and lib projects align.

/**
 * LiquidJS v9 → v10 template transformer.
 *
 * Pure string-to-string function that rewrites v9 Liquid template syntax
 * into v10-compatible equivalents. Applied to user-authored templates before
 * rendering on the v10 engine to preserve backward compatibility.
 *
 * v10's parser is stricter than v9's. v9 silently accepted many malformed
 * constructs that v10 rejects at parse time. This transformer handles both
 * intentional v9→v10 behavioral changes AND common malformed-template patterns
 * that v9's lenient parser tolerated.
 *
 * === v9→v10 behavioral changes ===
 *
 * 1. **Empty-value-before-pipe** — v9 accepts `{% assign x = |filter %}` and
 *    `{{ |filter }}` (empty LHS piped through filter); v10 rejects this as
 *    `invalid value expression`. We insert `_v9_undef_` before the pipe.
 *
 * 2. **Filter+comparison in conditionals** — v9 accepts
 *    `{% if val | cast:'number' > 10 %}` (filters with arguments followed by
 *    comparison); v10 throws a parse error. We rewrite to
 *    `{% assign tmp = val | cast:'number' %}{% if tmp > 10 %}`.
 *
 * 3. **concat+split array stringification** — `| concat:` produces an array;
 *    when piped to `| split: ','`, v9 stringifies with commas before splitting
 *    (producing N elements), while v10 concatenates without separator (producing
 *    1 element). We insert `| join: ','` between concat and split.
 *
 * === v9-lenient syntax that v10 rejects ===
 *
 * 4. **Smart/curly quotes** — v9 accepts `"` `"` in filter args; v10 rejects.
 * 5. **Double-dash trim** — v9 accepts `--%}`; v10 requires `-%}`.
 * 6. **Block-close in output tag** — v9 accepts `{{ expr -%}}`; v10 rejects
 *    the `%` in output context. We rewrite to `{{ expr -}}`.
 * 7. **Stray `}` before `|`** — v9 ignores a lone `}` mid-filter-chain; v10
 *    rejects it. We strip the stray brace.
 * 8. **Missing `]` (using `}` instead)** — v9 accepts `row['field'}`;
 *    v10 requires `row['field']`.
 * 9. **Empty output tags** — v9 outputs `""` for `{{ }}` / `{{- -}}`;
 *    v10 rejects them. We insert `""`.
 * 10. **Quoted filter name** — v9 silently ignores `| 'date: %Y'` (filter
 *     never applied, value passes through). v10 rejects it. We remove the
 *     broken filter reference to preserve v9's pass-through behavior.
 * 11. **Double pipe** — In block tags (`{% %}`), `||` means logical OR; we
 *     rewrite to `or`. In output tags (`{{ }}`), `||` is a fallback; we
 *     rewrite to `| default:`. `| |` (with space) normalizes to single `|`.
 * 12. **`| split` with no argument** — v9 wraps the value in a single-element
 *     array; v10 splits on every character boundary. We rewrite to use `push`
 *     to wrap the value in an array.
 * 13. **Duplicated else** — v9 ignores `{%- else -%}{%- else -%}`; v10 rejects.
 *     We strip the duplicate.
 * 14. **Missing `|` between filters** — v9 ignores the second filter; v10
 *     rejects. We insert the missing pipe.
 * 15. **Nested output tag in else** — v9 ignores `{{ '' }}` inside
 *     `{%- else ... -%}`; v10 rejects. We strip the nested tag.
 * 16. **Missing `]`** — v9 accepts `row['field' != '']`; v10 rejects unclosed
 *     bracket. We insert the missing `]`.
 * 17. **Filter on control flow tag** — v9 ignores `{% endif | cast %}`;
 *     v10 rejects. We strip the filter.
 * 18. **Parenthesized sub-expressions** — Liquid doesn't support parens.
 *     v9 passes `(expr` as-is. We extract `(expr | filter)` to assigns.
 * 19. **Spread `...`** — v9 ignores `...` in argument lists; v10 rejects.
 *     We strip the `...` tokens.
 *
 * Additionally, nil is rewritten to `_v9_undef_` defensively (see Step 3).
 */

/**
 * Transform a v9 LiquidJS template to be compatible with v10.
 */
export function transformV9ToV10(template: string): string {
  // Protect {% raw %}...{% endraw %} blocks from transformation.
  // Content inside raw blocks is literal text that Liquid outputs verbatim;
  // the transformer must not rewrite it.
  const rawBlocks: string[] = [];
  let result = template.replace(
    /(\{%-?\s*raw\s*-?%\}[\s\S]*?\{%-?\s*endraw\s*-?%\})/g,
    (block) => {
      rawBlocks.push(block);
      return `__v9_raw_${rawBlocks.length - 1}__`;
    },
  );

  // ── Pre-processing: normalize syntax that v9 tolerated but v10 rejects ──

  // Step 0a: Normalize smart/curly quotes to straight quotes inside Liquid tags.
  // v9 accepts Unicode quotes (\u201C \u201D \u2018 \u2019) in filter arguments;
  // v10 rejects standalone smart quotes but handles them fine when they appear
  // as literal content inside an already-quoted string (e.g. "\u201cfoo\u201d"
  // renders identically on v9 and v10). We only replace smart quotes that are
  // used as standalone delimiters — i.e. NOT already inside a '...' or "..." string.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) => {
    // Replace smart double quotes only when NOT inside a "..." string
    tag = tag.replace(/\u201C|\u201D/g, (match, offset) => {
      const before = tag.slice(0, offset);
      const dblQuotes = (before.match(/"/g) || []).length;
      const sglQuotes = (before.match(/'/g) || []).length;
      // If inside a quoted string (odd count of the enclosing quote), keep as-is
      if (dblQuotes % 2 === 1 || sglQuotes % 2 === 1) return match;
      return '"';
    });
    // Replace smart single quotes only when NOT inside a '...' string
    tag = tag.replace(/\u2018|\u2019/g, (match, offset) => {
      const before = tag.slice(0, offset);
      const sglQuotes = (before.match(/'/g) || []).length;
      const dblQuotes = (before.match(/"/g) || []).length;
      if (sglQuotes % 2 === 1 || dblQuotes % 2 === 1) return match;
      return "'";
    });
    return tag;
  });

  // Step 0b: Double-dash trim: `--%}` → `-%}`.
  // v9 ignores the extra dash; v10 rejects it.
  result = result.replace(/--%\}/g, "-%}");

  // Step 0c: Block-close syntax in output tag: `-%}}` → `-}}`.
  // v9 accepts `{{ expr -%}}` (mixing block-close -%} with output }});
  // v10 rejects the `%` in output context. Valid trim close is `-}}`.
  result = result.replace(/-%\}\}/g, "-}}");

  // Step 0d: Stray single `}` inside Liquid tags.
  // v9 ignores a lone `}` mid-chain; v10 rejects it.
  // Patterns: `} |` (before filter pipe), `}-%}` or `} -%}` (before block close),
  // `} }}` (before output close), `},` (before comma in args).
  // Exclude `%` to avoid eating the `}` from `%}` tag closes.
  // Process inside tags only to avoid stripping `}` from literal text.
  // Must be string-aware: don't strip `}` inside quoted string arguments.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) => {
    return stripStrayBraces(tag);
  });

  // Step 0e: Missing close bracket — `}` instead of `]` in property accessors.
  // v9 is lenient with `row['field'}` or `row["field"}`; v10 requires `]`.
  // Handle `["field"}}` first (preserve output tag close `}}`), then lone `}`.
  // Also allow optional whitespace between the quote and `}` (e.g. `["field" }}`).
  result = result.replace(/(\['[^']*')\s*\}\}/g, "$1]}}");
  result = result.replace(/(\["[^"]*")\s*\}\}/g, "$1]}}");
  result = result.replace(/(\['[^']*')\s*\}(?!\})/g, "$1]");
  result = result.replace(/(\["[^"]*")\s*\}(?!\})/g, "$1]");

  // Step 0f: Empty output tags — `{{ }}`, `{{- -}}`, `{{-}}`, etc.
  // v9 outputs empty string for empty expressions; v10 rejects them.
  result = result.replace(/\{\{(-?)\s*(-?)\}\}/g, '{{$1 "" $2}}');

  // Step 0g: Quoted filter name — `| 'date: %Y'` or `| "value"` before `}}`.
  // v9 silently ignores quoted text after pipe (filter never applied, value
  // passes through). We remove the broken filter reference to match v9 behavior.
  result = result.replace(/\|\s*(?:'[^']*'|"[^"]*")\s*(?=-?\}\})/g, "");

  // Step 0h: Double pipe `||` or `| |`.
  // In block tags `{% %}`: `||` is used as logical OR; rewrite to `or`.
  // In output tags `{{ }}`: `||` means "fallback" (v9 treats as single `|`,
  // which makes the RHS a non-existent filter that's silently ignored, so only
  // the LHS value is output). Rewrite `expr || fallback` to `expr | default: fallback`
  // for correct v10 semantics. `| |` (with space) is just a stutter → single `|`.
  result = result.replace(/\{%[\s\S]*?%\}/g, (tag) =>
    tag.replace(/\|\|/g, " or ").replace(/\|\s+\|/g, "|"),
  );
  result = result.replace(/\{\{[\s\S]*?\}\}/g, (tag) => {
    // `||` followed by property access (row['x'], context.y) → `| default:` (fallback)
    // `||` followed by filter name (downcase, upcase) → `|` (double pipe typo)
    let fixed = tag.replace(
      /\|\|\s*(?=(?:row|merged|context)\b)/g,
      "| default: ",
    );
    // Remaining `||` or `| |` → single `|` (double pipe / stutter)
    fixed = fixed.replace(/\|\s*\|/g, "|");
    return fixed;
  });

  // Step 0i: Rewrite `| split` with no argument (no delimiter).
  // v9 wraps the value in a single-element array (String(undefined)="undefined",
  // no match → ["val"]). v10 splits on every character boundary because
  // stringify(undefined)="" matches between every char.
  // Rewrite: extract to temp var using `_v9_undef_ | push: expr` to create ["expr"].
  // Only matches when `| split` has no `:` argument and the expression before
  // the pipe contains no other filters (no other `|`).
  let splitCounter = 0;
  // Handle output tags: {{ expr | split }} → {% assign __v9_split_N = _v9_undef_ | push: expr %}{{ __v9_split_N }}
  result = result.replace(
    /\{\{(-?)([^|]*?)\|\s*split\s*(-?\}\})/g,
    (_match, wsL, beforeSplit, wsR) => {
      const expr = beforeSplit.trim();
      if (!expr) return _match;
      const tmp = `__v9_split_${splitCounter++}`;
      return `{% assign ${tmp} = _v9_undef_ | push: ${expr} %}{{${wsL} ${tmp} ${wsR}`;
    },
  );
  // Handle assign tags: {% assign x = expr | split %} → {% assign x = _v9_undef_ | push: expr %}
  result = result.replace(
    /(\{%-?\s*assign\s+\w+\s*=\s*)([^|]*?)\|\s*split(\s*-?%\})/g,
    (_match, prefix, beforeSplit, suffix) => {
      const expr = beforeSplit.trim();
      if (!expr) return _match;
      return `${prefix}_v9_undef_ | push: ${expr}${suffix}`;
    },
  );

  // Step 0j: Duplicated `{%- else -%}` — v9 ignores the second else; v10 rejects.
  // Strip the duplicate else tag.
  result = result.replace(
    /(\{%-?\s*else\s*-?%\})\s*\{%-?\s*else\s*-?%\}/g,
    "$1",
  );

  // Step 0k: Missing `|` between filters — e.g. `append: row['x'] append: 'y'`.
  // v9 ignores the second filter; v10 rejects. Insert the missing pipe.
  // The preceding char must be a value-ending char (letter, digit, quote, bracket)
  // to avoid false positives on `| filter |  split:` where whitespace after `|`
  // would match `[^|]`.
  result = result.replace(
    /(\{\{[\s\S]*?\}\})/g,
    (tag) => tag.replace(/([\]'"a-zA-Z0-9_])\s+(append|prepend|replace|remove|upcase|downcase|strip|default|date|size|first|last|join|split|sort|reverse|map|compact|uniq|where|concat|slice|truncate|truncatewords|capitalize|escape|url_encode|url_decode|strip_html|strip_newlines|newline_to_br|plus|minus|times|divided_by|modulo|round|ceil|floor|abs|cast|json_construct|array_construct|regex_replace|remove_empty):/g, "$1 | $2:"),
  );

  // Step 0l: `{%- else {{ '' }} -%}` — nested output tag inside else tag.
  // v9 ignores the `{{ '' }}`; v10 rejects it. Strip the nested output tag.
  result = result.replace(
    /(\{%-?\s*else)\s*\{\{[^}]*\}\}\s*(-?%\})/g,
    "$1 $2",
  );

  // Step 0m: Missing `]` on property accessor — `row['field' != '']` or `row['field' |`.
  // v9 is lenient; v10 requires the closing bracket.
  // Must be string-aware: only match `[` that starts a property accessor, not `[`
  // inside string literals like `append: '": ["'`.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) =>
    insertMissingCloseBracket(tag),
  );

  // Step 0m2: Missing closing quote and bracket — `row['field_name` with no `']`.
  // v9 is lenient; v10 rejects the unclosed `[`. Insert `']` before the tag close.
  // This is handled inside each Liquid tag to avoid false positives with `|` inside
  // quoted property names like `row['Cell_Code|Cell_Name']`.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) =>
    closeUnclosedBracketString(tag),
  );

  // Step 0n: Filter on control flow tag — `{% endif | cast: 'boolean' %}`.
  // v9 ignores the filter; v10 rejects it. Strip the filter from endif/endfor/etc.
  // Use `[\s\S]*?` (non-greedy) instead of `[^%]*` to correctly preserve the
  // whitespace trim marker `-` before `%}`. The old `[^%]*` greedily consumed
  // the `-` from `-%}` because `-` ≠ `%`, losing the right trim.
  result = result.replace(
    /(\{%-?\s*(?:endif|endunless|endfor|endcase))\s*\|[\s\S]*?(-?%\})/g,
    "$1 $2",
  );

  // Step 0o: Parenthesized sub-expressions — `(expr | filter)`.
  // Liquid doesn't support parens. v9 passes `(expr` as-is (often NaN/garbage).
  // Extract to assign to match v9 behavior as closely as possible.
  // Must be string-aware: `(regex|alt)` inside a quoted filter argument like
  // `regex_test: '^...(a|b)...'` must NOT be extracted.
  let parenCounter = 0;
  result = result.replace(
    /(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g,
    (tag) => {
      const parenRe = /\(([^()]+\|[^()]+)\)/g;
      if (!parenRe.test(tag)) return tag;
      parenRe.lastIndex = 0;
      const assigns: string[] = [];
      const fixed = tag.replace(parenRe, (m, inner: string, offset: number) => {
        if (isInsideString(tag, offset)) return m;
        const tmp = `__v9_paren_${parenCounter++}`;
        assigns.push(`{% assign ${tmp} = ${inner.trim()} %}`);
        return tmp;
      });
      if (assigns.length === 0) return tag;
      return assigns.join("") + fixed;
    },
  );

  // Step 0p: Stray `]` after filter argument before `|`, `}}`, or `%}`.
  // e.g. `| join: '' ] | default: 0` — v9 ignores the `]`; v10 rejects it.
  // Also handles: `| truncate: 3] }}` — stray `]` before output close.
  // Only match when `]` follows a filter argument (after `:` + value),
  // NOT when it follows a property accessor `['field'] |`.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) =>
    tag
      .replace(/(:\s*['"][^'"]*['"])\s*\]\s*(\||-?%\}|-?\}\})/g, "$1 $2")
      .replace(/(:\s*\d+)\s*\]\s*(\||-?%\}|-?\}\})/g, "$1 $2"),
  );

  // Step 0q: `{%- else EXPR %}` — else tag with spurious argument.
  // e.g. `{%- else 0%}` — v9 ignores; v10 rejects. Strip the argument.
  result = result.replace(
    /(\{%-?\s*else)\s+(?!if\b)[^%-]+(-?%\})/g,
    "$1 $2",
  );

  // Step 0r: Missing comma between filter arguments — `replace: '"' '\"'`.
  // v9 accepts space-separated args in some contexts; v10 requires commas.
  // Insert a comma between two adjacent string-literal arguments.
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) =>
    tag.replace(
      /('(?:[^'\\]|\\.)*')\s+('(?:[^'\\]|\\.)*')(?=\s*(?:\||-?\}\}|-?%\}))/g,
      "$1, $2",
    ),
  );

  // Step 0s: Spread operator `...` in filter argument lists.
  // Not valid Liquid. v9 ignores `...` tokens; v10 rejects them.
  // Strip `, ...,` → `,` (middle), `..., ` → `` (leading).
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) =>
    tag
      .replace(/,\s*\.\.\.\s*,/g, ",")
      .replace(/\.\.\.\s*,\s*/g, "")
      .replace(/,\s*\.\.\./g, ""),
  );

  // ── Core v9→v10 behavioral changes ──

  // Step 1a: Rewrite `{% assign x = |filter %}` → `{% assign x = _v9_undef_ | filter %}`.
  // v9 accepts an empty value expression before a pipe; v10 rejects it.
  // We use `_v9_undef_` (an undefined variable) rather than `nil` because v10
  // with keepOutputType passes `{}` (truthy) for nil in production, which breaks
  // filters like array_construct that check `!val`. An undefined variable
  // passes `undefined` (falsy), matching v9's empty-LHS behavior exactly.
  result = result.replace(
    /(\{%-?\s*assign\s+\w+\s*=)\s*(\|)/g,
    "$1 _v9_undef_ $2",
  );

  // Step 1b: Rewrite `{{ |filter }}` → `{{ _v9_undef_ | filter }}`.
  // Same breaking change as 1a but in output tags instead of assign tags.
  // v9 accepts an empty value expression before a pipe in output tags; v10 rejects it.
  result = result.replace(/(\{\{-?\s*)(\|)/g, "$1_v9_undef_ $2");

  // Step 2: Rewrite filter+comparison in if/elsif/unless conditionals.
  result = rewriteFilterComparisons(result);

  // Step 3: Rewrite `{{ nil | filter }}` → `{{ _v9_undef_ | filter }}`.
  result = result.replace(/\{\{(-?\s*)nil(\s*\|)/g, "{{$1_v9_undef_$2");

  // NOTE: We deliberately do NOT inject `| join: ','` on bare output tags to
  // reproduce v9's array→"a,b,c" coercion. On scalar columns the join is a
  // no-op; on genuine array columns the v9→v10 difference ("a,b,c" vs "abc")
  // is a real output change that should surface as an output_diff in the
  // migration UI, not be silently rewritten. The runtime dual-render serves
  // v9 (and pins) on any real mismatch. See PLAT-1211.

  // Step 4: Insert `| join: ','` before `| split:` when `| concat:` precedes it.
  // v9 stringifies arrays with commas before passing to `split`; v10 concatenates
  // without separator. Adding an explicit join restores v9's comma-separated
  // stringification so `| split: ','` produces the same result on both engines.
  // Only matches within a single filter chain (same tag).
  result = result.replace(/(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\})/g, (tag) => {
    if (!tag.includes("| concat") && !tag.includes("|concat")) return tag;
    // Insert `| join: ','` immediately before `| split:` in the filter chain.
    return tag.replace(/(\|\s*concat\b[^|]*?)(\|\s*split\s*:)/g, "$1| join: ',' $2");
  });

  // Restore {% raw %}...{% endraw %} blocks.
  for (let i = 0; i < rawBlocks.length; i++) {
    result = result.replace(`__v9_raw_${i}__`, rawBlocks[i]);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Filter+comparison rewrite
// ---------------------------------------------------------------------------

/**
 * Match a filter+comparison pattern in a sub-condition string.
 * Handles filters with arguments (e.g. `cast:'number'`) and without (`size`).
 *
 * Returns the filter expression, comparison operator, and RHS value,
 * or null if no filter+comparison is found.
 */
function matchFilterComparison(
  cond: string,
): { filterExpr: string; op: string; rhs: string } | null {
  // Permissive regex: (expr | filter_chain) (comparison) (rhs)
  // .+? after | is non-greedy so it extends only until a comparison operator.
  const m = cond.match(/^(.+\|.+?)\s+(==|!=|>=|<=|>|<|contains)\s+(.+)$/);
  if (!m) return null;

  // Guard: the last pipe must not be inside a string literal.
  const filterExpr = m[1];
  const lastPipe = filterExpr.lastIndexOf("|");
  if (lastPipe === -1 || isInsideString(filterExpr, lastPipe)) return null;

  return { filterExpr: m[1].trim(), op: m[2], rhs: m[3].trim() };
}

/**
 * Check if a position in a string is inside a quoted string literal.
 */
function isInsideString(str: string, pos: number): boolean {
  let inString = false;
  let stringChar = "";
  for (let i = 0; i < pos; i++) {
    const ch = str[i];
    if (inString) {
      if (ch === stringChar) inString = false;
    } else if (ch === '"' || ch === "'") {
      inString = true;
      stringChar = ch;
    }
  }
  return inString;
}

/**
 * Split a conditional expression on `and`/`or` logical operators,
 * respecting string literals.
 */
function splitOnLogicalOps(
  cond: string,
): Array<{ type: "cond" | "op"; text: string }> {
  const opPositions: Array<{
    start: number;
    end: number;
    op: string;
  }> = [];
  const re = /\b(and|or)\b/g;
  for (let m = re.exec(cond); m !== null; m = re.exec(cond)) {
    if (!isInsideString(cond, m.index)) {
      opPositions.push({
        start: m.index,
        end: m.index + m[0].length,
        op: m[1],
      });
    }
  }

  if (opPositions.length === 0) {
    return [{ type: "cond", text: cond }];
  }

  const result: Array<{ type: "cond" | "op"; text: string }> = [];
  let cursor = 0;
  for (const op of opPositions) {
    result.push({
      type: "cond",
      text: cond.substring(cursor, op.start).trim(),
    });
    result.push({ type: "op", text: op.op });
    cursor = op.end;
  }
  result.push({ type: "cond", text: cond.substring(cursor).trim() });

  return result;
}

function rewriteFilterComparisons(template: string): string {
  const tagRe = /\{%-?\s*(if|elsif|unless)\s+([\s\S]*?)\s*-?%\}/g;

  type Op = {
    tagStart: number;
    tagEnd: number;
    newTag: string;
    assignPos: number;
    assignText: string;
  };

  const ops: Op[] = [];
  let counter = 0;

  for (let m = tagRe.exec(template); m !== null; m = tagRe.exec(template)) {
    const [fullTag, keyword, rawCond] = m;

    // Split on and/or, check each sub-condition for filter+comparison
    const parts = splitOnLogicalOps(rawCond.trim());
    const assigns: string[] = [];
    const newParts: string[] = [];
    let modified = false;

    for (const part of parts) {
      if (part.type === "op") {
        newParts.push(part.text);
        continue;
      }

      const comp = matchFilterComparison(part.text.trim());
      if (comp) {
        const tmpVar = `__v9_compat_${counter++}`;
        assigns.push(`{% assign ${tmpVar} = ${comp.filterExpr} %}`);
        newParts.push(`${tmpVar} ${comp.op} ${comp.rhs}`);
        modified = true;
      } else {
        newParts.push(part.text);
      }
    }

    if (!modified) continue;

    const wsL = fullTag.match(/^\{%-?/)![0];
    const wsR = fullTag.match(/-?%\}$/)![0];
    const newCond = newParts.join(" ");
    const newTag = `${wsL} ${keyword} ${newCond} ${wsR}`;
    const assignText = assigns.join("");

    const assignPos =
      keyword === "elsif" ? findParentIfStart(template, m.index) : m.index;

    ops.push({
      tagStart: m.index,
      tagEnd: m.index + fullTag.length,
      newTag,
      assignPos,
      assignText,
    });
  }

  if (ops.length === 0) return template;

  type Edit =
    | { kind: "insert"; pos: number; text: string }
    | { kind: "replace"; start: number; end: number; text: string };

  const edits: Edit[] = [];
  for (const op of ops) {
    if (op.assignPos === op.tagStart) {
      edits.push({
        kind: "replace",
        start: op.tagStart,
        end: op.tagEnd,
        text: op.assignText + op.newTag,
      });
    } else {
      edits.push({
        kind: "insert",
        pos: op.assignPos,
        text: op.assignText,
      });
      edits.push({
        kind: "replace",
        start: op.tagStart,
        end: op.tagEnd,
        text: op.newTag,
      });
    }
  }

  edits.sort((a, b) => {
    const pa = a.kind === "insert" ? a.pos : a.start;
    const pb = b.kind === "insert" ? b.pos : b.start;
    return pa - pb;
  });

  let out = "";
  let cursor = 0;
  for (const e of edits) {
    if (e.kind === "insert") {
      out += template.substring(cursor, e.pos) + e.text;
      cursor = e.pos;
    } else {
      out += template.substring(cursor, e.start) + e.text;
      cursor = e.end;
    }
  }
  out += template.substring(cursor);
  return out;
}

/**
 * Strip stray `}` from inside a Liquid tag, respecting string literals.
 * A stray `}` is one that appears outside quotes and before `|`, `-%}`, `}}`, or `,`.
 */
function stripStrayBraces(tag: string): string {
  // Parse the tag character by character, tracking string state.
  // Build a list of positions of stray `}` to remove.
  const removals = new Set<number>();
  let inString = false;
  let stringChar = "";
  let braceDepth = 0; // track `{`...`}` pairs (hash literals like {url: url})

  // Determine tag boundaries: skip the opening `{{`/`{%` and closing `}}`/`%}`
  const isOutput = tag.startsWith("{{");
  const openLen = isOutput
    ? tag.match(/^\{\{-?/)![0].length
    : tag.match(/^\{%-?/)![0].length;
  const closeLen = isOutput
    ? tag.match(/-?\}\}$/)![0].length
    : tag.match(/-?%\}$/)![0].length;
  const inner = tag.substring(openLen, tag.length - closeLen);

  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (inString) {
      if (ch === stringChar) inString = false;
      continue;
    }
    if (ch === "'" || ch === '"') {
      inString = true;
      stringChar = ch;
      continue;
    }
    if (ch === "{") {
      braceDepth++;
      continue;
    }
    if (ch !== "}") continue;

    // If we're inside a `{`...`}` pair (hash literal like {url: url}),
    // this `}` closes it — don't strip.
    if (braceDepth > 0) {
      braceDepth--;
      continue;
    }

    // Unmatched `}` outside strings and hash literals is stray syntax
    // that v9 ignored. Mark for removal.
    removals.add(i + openLen);
  }

  if (removals.size === 0) return tag;

  let result = "";
  for (let i = 0; i < tag.length; i++) {
    if (!removals.has(i)) result += tag[i];
  }
  return result;
}

/**
 * Insert missing `]` on property accessors inside a single Liquid tag.
 * Handles `row['field' !=` and `row['field' |` patterns while respecting
 * string literal boundaries — won't match `[` that appears inside quotes.
 */
function insertMissingCloseBracket(tag: string): string {
  // Parse character by character, tracking string state and bracket state.
  const chars = [...tag];
  const insertions: number[] = []; // positions where `]` should be inserted

  let inString = false;
  let stringChar = "";
  let bracketStart = -1; // position of `[` that opened current accessor
  let sawQuote = false; // whether we've seen a quoted key inside the bracket
  let quoteEnded = false; // whether the quoted key has been closed

  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];

    if (inString) {
      if (ch === stringChar) {
        inString = false;
        if (bracketStart >= 0) {
          quoteEnded = true;
        }
      }
      continue;
    }

    if (ch === "'" || ch === '"') {
      inString = true;
      stringChar = ch;
      if (bracketStart >= 0 && !sawQuote) {
        sawQuote = true;
        quoteEnded = false;
      }
      continue;
    }

    if (ch === "[") {
      bracketStart = i;
      sawQuote = false;
      quoteEnded = false;
      continue;
    }

    if (ch === "]") {
      // Properly closed bracket — reset state.
      bracketStart = -1;
      sawQuote = false;
      quoteEnded = false;
      continue;
    }

    // If we're inside a bracket that has a completed quoted key, and we
    // hit a comparison operator or pipe, the `]` was omitted.
    if (bracketStart >= 0 && sawQuote && quoteEnded) {
      const rest = tag.substring(i);
      if (/^(!=|==|>=|<=|>|<|\|)/.test(rest) || /^\s+(!=|==|>=|<=|>|<|\|)/.test(rest)) {
        // Find the insertion point: right after the closing quote
        // (skip any whitespace between quote and operator).
        let insertAt = i;
        // Walk back to skip whitespace
        while (insertAt > 0 && /\s/.test(chars[insertAt - 1])) {
          insertAt--;
        }
        insertions.push(insertAt);
        bracketStart = -1;
        sawQuote = false;
        quoteEnded = false;
      }
    }
  }

  if (insertions.length === 0) return tag;

  // Build result with insertions (process in reverse to preserve positions)
  let result = tag;
  for (let i = insertions.length - 1; i >= 0; i--) {
    const pos = insertions[i];
    result = result.substring(0, pos) + "]" + result.substring(pos);
  }
  return result;
}

/**
 * Close unclosed `['field_name` patterns where the opening quote never closes.
 * e.g. `{{ merged['some_field }}` → `{{ merged['some_field'] }}`
 *
 * Uses character-by-character parsing to correctly handle `|` inside quoted
 * property names (e.g. `row['Cell_Code|Cell_Name']` must NOT be modified).
 */
function closeUnclosedBracketString(tag: string): string {
  const isOutput = tag.startsWith("{{");
  const openLen = isOutput
    ? tag.match(/^\{\{-?/)![0].length
    : tag.match(/^\{%-?/)![0].length;
  const closeLen = isOutput
    ? tag.match(/-?\}\}$/)![0].length
    : tag.match(/-?%\}$/)![0].length;
  const inner = tag.substring(openLen, tag.length - closeLen);

  let inString = false;
  let stringChar = "";
  let bracketStart = -1;
  let bracketQuoteStart = -1; // position of the opening `'` or `"` inside bracket

  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i];
    if (inString) {
      if (ch === stringChar) {
        inString = false;
        bracketQuoteStart = -1; // quote closed normally — not an unclosed bracket
      }
      continue;
    }
    if (ch === "'" || ch === '"') {
      inString = true;
      stringChar = ch;
      if (bracketStart >= 0 && bracketQuoteStart < 0) {
        bracketQuoteStart = i;
      }
      continue;
    }
    if (ch === "[") {
      bracketStart = i;
      bracketQuoteStart = -1;
      continue;
    }
    if (ch === "]") {
      bracketStart = -1;
      bracketQuoteStart = -1;
      continue;
    }
  }

  // If we exit with an open string inside a bracket, the quote never closed.
  // Insert `']` (or `"]`) before the tag close.
  if (bracketStart >= 0 && inString && bracketQuoteStart >= 0) {
    const closeQuote = stringChar + "]";
    const insertPos = tag.length - closeLen;
    return tag.substring(0, insertPos) + closeQuote + tag.substring(insertPos);
  }

  return tag;
}

function findParentIfStart(template: string, elsifPos: number): number {
  const before = template.substring(0, elsifPos);
  const tags: Array<{ pos: number; kw: string }> = [];
  const re = /\{%-?\s*(if|unless|endif|endunless)\b/g;
  for (let m = re.exec(before); m !== null; m = re.exec(before)) {
    tags.push({ pos: m.index, kw: m[1] });
  }

  let nesting = 0;
  for (let i = tags.length - 1; i >= 0; i--) {
    const t = tags[i];
    if (t.kw === "endif" || t.kw === "endunless") {
      nesting++;
    } else if (t.kw === "if" || t.kw === "unless") {
      if (nesting > 0) nesting--;
      else return t.pos;
    }
  }
  return 0;
}
